// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Decisions Page
// Route: /decisions
// apps/web/src/routes/decisions.tsx
//
// Architectural Decision Records (ADRs) captured during development.
// Searchable by text, filterable by type and impact scope. Expandable
// cards showing context, rationale, rejected alternatives.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  EngineTooltip,
  MetricCard,
  EmptyState,
  LoadingShimmer,
  StatusBadge,
} from '../components/engine/EnginePageShell';
import { useCounter, useDebounce } from '../components/engine/hooks';
import { useDecisions } from '../lib/api/hooks-extended';
import type { Decision, DecisionType } from '../lib/api/types-extended';

export const Route = createFileRoute('/decisions')({
  component: DecisionsPage,
});

// ─── Decision Type Config ───────────────────────────────────────────────────

const DECISION_TYPE_CONFIG: Record<DecisionType, { color: string; icon: string }> = {
  Architecture: { color: C.violet, icon: '🏗' },
  Technology: { color: C.cyan, icon: '⚙' },
  Design: { color: C.pink, icon: '🎨' },
  Algorithm: { color: C.neon, icon: '🧮' },
  DataModel: { color: C.blue, icon: '🗃' },
  Security: { color: C.coral, icon: '🔒' },
  Infrastructure: { color: C.amber, icon: '☁' },
};

// Impact scope labels
const IMPACT_LABELS = ['', 'Minimal', 'Low', 'Moderate', 'High', 'Critical'];

// ─── Main Component ─────────────────────────────────────────────────────────

function DecisionsPage() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<DecisionType | 'All'>('All');
  const [minImpact, setMinImpact] = useState(0);
  const debouncedSearch = useDebounce(search, 250);

  const { data: decisions, loading } = useDecisions({
    search: debouncedSearch || undefined,
    minImpactScope: minImpact > 0 ? minImpact : undefined,
  });

  const filtered = useMemo(() => {
    if (!decisions) return [];
    if (typeFilter === 'All') return decisions;
    return decisions.filter((d) => d.decisionType === typeFilter);
  }, [decisions, typeFilter]);

  // ── Metrics ────────────────────────────────────────────────────────────

  const metrics = useMemo(() => {
    if (!decisions) return null;
    const byType: Record<string, number> = {};
    let highImpact = 0;
    for (const d of decisions) {
      byType[d.decisionType] = (byType[d.decisionType] || 0) + 1;
      if (d.impactScope >= 4) highImpact++;
    }
    const avgImpact = decisions.length > 0
      ? decisions.reduce((s, d) => s + d.impactScope, 0) / decisions.length
      : 0;
    return { total: decisions.length, highImpact, avgImpact, byType };
  }, [decisions]);

  const countTotal = useCounter(metrics?.total ?? 0, 600, 200);
  const countHighImpact = useCounter(metrics?.highImpact ?? 0, 600, 300);

  if (loading) {
    return (
      <EnginePageShell title="Decisions" subtitle="ARCHITECTURE LOG" icon="🧭" accentColor={C.violet}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
          {[0,1,2].map(i => <GlassPanel key={i} delay={i*0.08}><LoadingShimmer height={50} /></GlassPanel>)}
        </div>
        {[0,1,2].map(i => <GlassPanel key={i} delay={0.3+i*0.1} style={{marginBottom:12}}><LoadingShimmer height={80} /></GlassPanel>)}
      </EnginePageShell>
    );
  }

  return (
    <EnginePageShell
      title="Decisions"
      subtitle="ARCHITECTURE LOG"
      icon="🧭"
      accentColor={C.violet}
      statusLabel={`${metrics?.total ?? 0} RECORDED`}
      statusColor={C.violet}
    >
      {/* ── Metrics ───────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
        <MetricCard label="Total Decisions" value={countTotal} icon="🧭" color={C.violet}
          tooltip={<div>Architectural decisions recorded across all projects and sessions.</div>} />
        <MetricCard label="High Impact" value={countHighImpact} icon="⚡" color={C.coral}
          subtitle="scope ≥ 4"
          tooltip={<div>Decisions with impact scope 4 (High) or 5 (Critical). These shape the fundamental architecture.</div>} />
        <MetricCard
          label="Avg Impact"
          value={(metrics?.avgImpact ?? 0).toFixed(1)}
          icon="📊" color={C.amber}
          subtitle={`/ 5.0`}
          tooltip={<div>Average impact scope across all decisions. Higher means more consequential decisions are being captured.</div>}
        />
      </div>

      {/* ── Search & Filters ──────────────────────────────────────────── */}
      <GlassPanel accent={C.violet} delay={0.3} style={{ marginBottom: 20, padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, opacity: 0.5 }}>🔍</span>
            <input
              type="text" placeholder="Search decisions, context, rationale..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%', background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`,
                borderRadius: 8, padding: '9px 12px 9px 36px', color: C.text,
                fontFamily: F.display, fontSize: 13, outline: 'none',
              }}
            />
          </div>

          {/* Type filter */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button
              onClick={() => setTypeFilter('All')}
              style={{
                background: typeFilter === 'All' ? `${C.violet}20` : 'transparent',
                border: `1px solid ${typeFilter === 'All' ? `${C.violet}50` : C.faint}`,
                color: typeFilter === 'All' ? C.violet : C.muted,
                fontFamily: F.mono, fontSize: 9, padding: '4px 10px', borderRadius: 5, cursor: 'pointer',
              }}
            >
              ALL
            </button>
            {(Object.keys(DECISION_TYPE_CONFIG) as DecisionType[]).map((t) => {
              const cfg = DECISION_TYPE_CONFIG[t];
              const active = typeFilter === t;
              return (
                <button
                  key={t}
                  onClick={() => setTypeFilter(t)}
                  style={{
                    background: active ? `${cfg.color}20` : 'transparent',
                    border: `1px solid ${active ? `${cfg.color}50` : C.faint}`,
                    color: active ? cfg.color : C.muted,
                    fontFamily: F.mono, fontSize: 9, padding: '4px 10px', borderRadius: 5, cursor: 'pointer',
                  }}
                >
                  {cfg.icon} {t.toUpperCase()}
                </button>
              );
            })}
          </div>

          {/* Impact filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim }}>MIN IMPACT</span>
            {[0, 1, 2, 3, 4, 5].map((level) => (
              <button
                key={level}
                onClick={() => setMinImpact(level)}
                style={{
                  width: 22, height: 22, borderRadius: 4, fontSize: 10, fontFamily: F.mono,
                  background: minImpact === level ? `${C.amber}25` : 'transparent',
                  border: `1px solid ${minImpact === level ? `${C.amber}50` : C.faint}`,
                  color: minImpact === level ? C.amber : C.dim,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {level || '∗'}
              </button>
            ))}
          </div>
        </div>
      </GlassPanel>

      {/* ── Decision List ─────────────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <EmptyState
          icon="🧭"
          title={decisions?.length === 0 ? 'No decisions recorded' : 'No matches'}
          subtitle={
            decisions?.length === 0
              ? 'Architectural Decision Records capture why you chose one approach over another. Critical for R&D documentation and future reference.'
              : 'Try broadening your search or adjusting filters.'
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((decision, i) => (
            <DecisionCard key={decision.id} decision={decision} index={i} />
          ))}
        </div>
      )}
    </EnginePageShell>
  );
}

// ─── DecisionCard ───────────────────────────────────────────────────────────

function DecisionCard({ decision, index }: { decision: Decision; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = DECISION_TYPE_CONFIG[decision.decisionType] || DECISION_TYPE_CONFIG.Architecture;
  const delay = 0.4 + index * 0.05;

  // Impact bar segments
  const impactColor =
    decision.impactScope >= 4 ? C.coral
    : decision.impactScope >= 3 ? C.amber
    : C.neon;

  return (
    <div
      onClick={() => setExpanded(!expanded)}
      style={{
        background: C.surface,
        border: `1px solid ${expanded ? `${cfg.color}25` : C.faint}`,
        borderRadius: 12,
        padding: '16px 20px',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        opacity: 0,
        animation: `engine-feed-in 0.4s cubic-bezier(0.16,1,0.3,1) ${delay}s forwards`,
        transition: 'border-color 0.2s',
      }}
    >
      {/* Type stripe */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: cfg.color, opacity: 0.4 }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <span style={{ fontSize: 18, marginTop: 2 }}>{cfg.icon}</span>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <StatusBadge label={decision.decisionType} color={cfg.color} />
              {decision.workItemName && (
                <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim }}>
                  📋 {decision.workItemName}
                </span>
              )}
              {decision.projectName && (
                <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim }}>
                  📂 {decision.projectName}
                </span>
              )}
            </div>

            {/* Decision text */}
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: C.text,
                lineHeight: 1.4,
                marginBottom: 4,
              }}
            >
              {decision.decision}
            </div>

            {/* Context preview */}
            {!expanded && decision.context && (
              <div
                style={{
                  fontSize: 12,
                  color: C.muted,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 600,
                }}
              >
                {decision.context}
              </div>
            )}
          </div>

          {/* Impact scope */}
          <EngineTooltip
            content={
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4, color: C.text }}>
                  Impact Scope: {decision.impactScope}/5 — {IMPACT_LABELS[decision.impactScope]}
                </div>
                <div style={{ fontSize: 11, color: C.textSoft }}>
                  How broadly this decision affects the system. 5 = changes fundamental architecture.
                </div>
              </div>
            }
          >
            <div style={{ textAlign: 'center', flexShrink: 0 }}>
              <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 4 }}>
                IMPACT
              </div>
              <div style={{ display: 'flex', gap: 2 }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: 6,
                      height: 18,
                      borderRadius: 2,
                      background: i < decision.impactScope ? impactColor : C.faint,
                      transition: 'background 0.2s',
                    }}
                  />
                ))}
              </div>
              <div style={{ fontSize: 9, fontFamily: F.mono, color: impactColor, marginTop: 3 }}>
                {IMPACT_LABELS[decision.impactScope]}
              </div>
            </div>
          </EngineTooltip>

          {/* Expand chevron */}
          <span
            style={{
              fontSize: 10, color: C.dim,
              transform: expanded ? 'rotate(90deg)' : 'rotate(0)',
              transition: 'transform 0.2s', marginTop: 4,
            }}
          >
            ▶
          </span>
        </div>

        {/* Expanded content */}
        {expanded && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.faint}`, animation: 'engine-tooltip-in 0.2s ease' }}>
            {/* Context */}
            {decision.context && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, fontFamily: F.mono, color: cfg.color, letterSpacing: '0.12em', marginBottom: 5 }}>
                  CONTEXT
                </div>
                <div style={{ fontSize: 13, color: C.textSoft, lineHeight: 1.6 }}>
                  {decision.context}
                </div>
              </div>
            )}

            {/* Rationale */}
            {decision.rationale && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, fontFamily: F.mono, color: C.neon, letterSpacing: '0.12em', marginBottom: 5 }}>
                  RATIONALE
                </div>
                <div style={{ fontSize: 13, color: C.textSoft, lineHeight: 1.6 }}>
                  {decision.rationale}
                </div>
              </div>
            )}

            {/* Alternatives considered */}
            {decision.alternatives && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, fontFamily: F.mono, color: C.coral, letterSpacing: '0.12em', marginBottom: 5 }}>
                  REJECTED ALTERNATIVES
                </div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, fontStyle: 'italic' }}>
                  {decision.alternatives}
                </div>
              </div>
            )}

            {/* Tags + metadata */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {decision.expertiseDomain && (
                <span style={{
                  fontSize: 9, fontFamily: F.mono, color: C.violet,
                  background: `${C.violet}10`, border: `1px solid ${C.violet}20`,
                  padding: '2px 8px', borderRadius: 4,
                }}>
                  {decision.expertiseDomain}
                </span>
              )}
              {decision.tags?.map((tag) => (
                <span key={tag} style={{
                  fontSize: 9, fontFamily: F.mono, color: C.dim,
                  background: `${C.cyan}08`, border: `1px solid ${C.cyan}15`,
                  padding: '2px 7px', borderRadius: 4,
                }}>
                  {tag}
                </span>
              ))}
              <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim, marginLeft: 'auto' }}>
                {new Date(decision.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
